import {normalizeKeywordPhrase} from '@/service/keywordStemDensity';
import {isPromotedPhraseFragment} from '@/service/keywordPhraseSplit';

const LETTER = 'a-záäčďéěíĺľňóôřšťúůýžA-ZÁÄČĎÉĚÍĹĽŇÓÔŘŠŤÚŮÝŽ';

function asText(value: unknown): string {
    return String(value || '').replace(/\s+/g, ' ').trim();
}

/**
 * User brief keyword wins over a divergent selectedKeyword (URL scrape / frequency pick).
 * Keep in sync with functions/src/OpenAI/articleMainKeyword.ts.
 */
export function resolveArticleMainKeyword(input: {
    keyword?: unknown;
    selectedKeyword?: unknown;
}): string {
    const brief = asText(input.keyword);
    const selected = asText(input.selectedKeyword);
    if (brief && selected) {
        if (normalizeKeywordPhrase(brief) === normalizeKeywordPhrase(selected)) {
            return brief;
        }
        return brief;
    }
    return brief || selected;
}

/** Scraped nav/legal titles and broken tokens must not become density rows or Lucy slots. */
export function isJunkDensityKeyword(phrase: unknown, mainKeyword = ''): boolean {
    const text = asText(phrase);
    if (!text) {
        return true;
    }
    if (/^[-–—|/\\.,:;]+$/.test(text)) {
        return true;
    }
    const letters = text.replace(new RegExp(`[^${LETTER}]`, 'g'), '');
    if (letters.length <= 1) {
        return true;
    }
    if (isPromotedPhraseFragment(text, mainKeyword)) {
        return true;
    }
    if (looksLikeGluedScrapePhrase(text)) {
        return true;
    }
    if (looksLikeScrapedNavTitle(text, mainKeyword)) {
        return true;
    }
    return false;
}

/**
 * A token is glued when two words were concatenated. The signals are structural
 * (digit/letter fusion, an internal capital, stacked function words, a short
 * preposition glued onto a vowel-initial word). Ordinary words are left alone.
 */
function gluedToken(token: string): boolean {
    const value = String(token || '').trim().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
    if (!value || value.length <= 3) {
        return false;
    }
    if (/^\d+[a-záčďéěíňóřšťúůýž]{2,}/i.test(value) || /[a-záčďéěíňóřšťúůýž]{3,}\d+[a-záčďéěíňóřšťúůýž]/i.test(value)) {
        return true;
    }
    if (/[a-záčďéěíňóřšťúůýž]{4,}[a-záčďéěíňóřšťúůýžA-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]*[a-záčďéěíňóřšťúůýž][A-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]/.test(value)) {
        return true;
    }
    if (value.length >= 18) {
        return true;
    }
    const lower = value.toLocaleLowerCase('cs');
    if (/^pro(?:za|na)\p{L}{6,}/u.test(lower)) {
        return true;
    }
    if (/^i(?:pro|po|do|na|za|od|ob|ke|se|ve)/.test(lower) && lower.length >= 8) {
        return true;
    }
    if (/^o[aeiouyáéěíóúůý]/.test(lower) && lower.length >= 8) {
        return true;
    }
    if (/^topř/.test(lower) && lower.length >= 7) {
        return true;
    }
    if (/^\p{L}{4,12}o[aeiouyáéěíóúůý]\p{L}{6,}$/u.test(lower)) {
        return true;
    }
    if (/^\p{L}{2,3}[dtk]a(?:po|pro)\p{L}{4,}$/u.test(lower)) {
        return true;
    }
    if (/^pro[bcčdďfghjklmnňpqrřsštťvwxzž][aeiouyáéěíóúůý]\p{L}{3,6}[éýá]$/u.test(lower) && lower.length <= 14) {
        return true;
    }
    return false;
}

export function looksLikeGluedScrapePhrase(text: unknown): boolean {
    const value = asText(text);
    if (!value) {
        return false;
    }
    if (!value.includes(' ')) {
        return gluedToken(value);
    }
    return value.split(/\s+/).some((part) => gluedToken(part));
}

export function looksLikeScrapedNavTitle(text: unknown, mainKeyword = ''): boolean {
    const value = asText(text);
    if (!value || value.length < 18) {
        return false;
    }
    const mainKey = normalizeKeywordPhrase(mainKeyword);
    const key = normalizeKeywordPhrase(value);
    if (mainKey && (key.includes(mainKey) || mainKey.includes(key))) {
        return false;
    }
    if (/\s[-–—]\s/.test(value) && value.length >= 24 && !/^(jak|co|proč|proc|how|what|why)\b/i.test(value)) {
        return true;
    }
    return false;
}

export function filterKeywordRows<T extends {keyword?: unknown}>(
    rows: T[],
    mainKeyword: string
): T[] {
    return rows.filter((row) => !isJunkDensityKeyword(row.keyword, mainKeyword));
}
